import { formatCurrency, formatTail, formatHumanDate } from '@/lib/format.js';
import { usePeekStore } from '@/stores/peek-store.js';
import { openCarrierPeek, openAircraftPeek, openContactPeek } from '@/lib/links.js';
import { Section, KV, RailStat } from './rail-kit.jsx';
import { toDate } from '@/lib/date-display.js';

// Right-rail for the account workspace (Task I.3; account-workspace.mockup.html
// → At a glance · Account · Carriers used · Aircraft summary · Trust balance).
// Pure presentational; reads the account's stats + resolve + fleet.

export default function AccountRail({ resolve, stats, fleetAndRoster }) {
  const openPeek = usePeekStore((s) => s.openPeek);
  const fleet = fleetAndRoster?.fleet || [];
  const current = fleet.filter((a) => a.status !== 'historical');
  const historical = fleet.filter((a) => a.status === 'historical');
  const carriers = stats?.carriers_used || [];
  const sinceYear = stats?.earliest_bound_start
    ? toDate(stats.earliest_bound_start)?.getFullYear()
    : null;

  return (
    <div className="text-text">
      <Section title="At a glance">
        <RailStat
          label="Lifetime premium"
          value={formatCurrency(stats?.lifetime_premium)}
          delta={stats?.bound_term_count ? `across ${stats.bound_term_count} term${stats.bound_term_count === 1 ? '' : 's'}` : null}
        />
        <RailStat label="Current premium" value={formatCurrency(stats?.current_premium)} />
        <RailStat
          label="Retention"
          value={stats?.retention_years != null ? `${stats.retention_years} yr${stats.retention_years === 1 ? '' : 's'}` : '—'}
          delta={sinceYear ? `since ${sinceYear}` : null}
        />
      </Section>

      <Section title="Account">
        {/* The primary opens the person sheet, where his name, email and phone are edited (AR17). */}
        <KV k="Primary contact" v={resolve?.primary_contact_name} onClick={resolve?.primary_contact_id ? () => openPeek(openContactPeek(resolve.primary_contact_id)) : null} />
        <KV k="Phone" v={resolve?.phone} />
        <KV k="Email" v={resolve?.email} />
        <KV k="Address" v={resolve?.billing_address} />
        <KV k="Lead source" v={resolve?.lead_source} />
      </Section>

      {carriers.length > 0 && (
        <Section title="Carriers used">
          {carriers.map((c) => (
            <KV
              key={c.name}
              k={c.name}
              v={`${c.terms} term${c.terms === 1 ? '' : 's'}${c.is_current ? ' · current' : ''}`}
              onClick={c.carrier_id ? () => openPeek(openCarrierPeek(c.carrier_id)) : null}
            />
          ))}
        </Section>
      )}

      {fleet.length > 0 && (
        <Section title="Aircraft summary">
          {current.map((a) => (
            <KV
              key={a.tail_number}
              k="Current"
              v={formatTail(a.tail_number)}
              onClick={a.tail_number_id ? () => openPeek(openAircraftPeek(a.tail_number_id)) : null}
            />
          ))}
          {historical.length > 0 && (
            <KV k="Historical" v={historical.map((a) => formatTail(a.tail_number)).join(', ')} />
          )}
        </Section>
      )}

      {stats?.trust_balance != null && (
        <Section title="Trust balance">
          <RailStat
            label="For this account"
            value={formatCurrency(stats.trust_balance)}
            accent={stats.trust_balance > 0}
            delta={
              stats.trust_last_reconciled_at
                ? `last reconciled ${formatHumanDate(stats.trust_last_reconciled_at)}`
                : null
            }
          />
        </Section>
      )}
    </div>
  );
}
